import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { StarIcon, ShieldCheckIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function TestimonialsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="w-full bg-dark-section py-20 md:py-28" data-id="element-359">
      <div className="max-w-4xl mx-auto px-6" data-id="element-360">
        <motion.h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-white text-center mb-4 tracking-wide" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6
      }} data-id="element-361">
          WHAT <span className="text-orange-accent" data-id="element-362">OTHERS</span> ARE SAYING
        </motion.h2>

        <motion.p className="font-inter text-concrete text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} data-id="element-363">
          <ShieldCheckIcon className="w-4 h-4 inline mr-2 text-orange-accent" data-id="element-364" />
          Names changed to protect privacy — as it should be
        </motion.p>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-365">
          {/* Testimonial 1 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-white/5 p-6" data-id="element-366">
            <div className="flex gap-1 mb-4" data-id="element-367">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-orange-accent text-orange-accent" data-id="element-368" />)}
            </div>
            <p className="font-inter text-concrete leading-relaxed mb-4" data-id="element-369">
              "Built mine in the garage floor over a long weekend. My wife
              didn't even know it was there until I showed her.{' '}
              <span className="text-white" data-id="element-370">That's exactly the point.</span> The
              neighbor's house got hit last month — they took his Liberty safe
              with a dolly. Mine? Invisible."
            </p>
            <div className="flex items-center gap-3" data-id="element-371">
              <div className="w-10 h-10 bg-orange-accent/20 rounded-full flex items-center justify-center" data-id="element-372">
                <span className="font-bebas text-orange-accent" data-id="element-373">M.R.</span>
              </div>
              <div data-id="element-374">
                <div className="font-inter text-white text-sm font-semibold" data-id="element-375">
                  Mike R.
                </div>
                <div className="font-inter text-concrete/60 text-xs" data-id="element-376">
                  Texas • Verified Buyer
                </div>
              </div>
            </div>
          </motion.div>

          {/* Testimonial 2 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-white/5 p-6" data-id="element-377">
            <div className="flex gap-1 mb-4" data-id="element-378">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-orange-accent text-orange-accent" data-id="element-379" />)}
            </div>
            <p className="font-inter text-concrete leading-relaxed mb-4" data-id="element-380">
              "I was skeptical about the $500 claim. Ended up spending $487 at
              Home Depot. The guide is incredibly detailed —{' '}
              <span className="text-white" data-id="element-381">
                every SKU, every measurement, every step.
              </span>{' '}
              No guesswork. Just follow the blueprint."
            </p>
            <div className="flex items-center gap-3" data-id="element-382">
              <div className="w-10 h-10 bg-orange-accent/20 rounded-full flex items-center justify-center" data-id="element-383">
                <span className="font-bebas text-orange-accent" data-id="element-384">J.T.</span>
              </div>
              <div data-id="element-385">
                <div className="font-inter text-white text-sm font-semibold" data-id="element-386">
                  James T.
                </div>
                <div className="font-inter text-concrete/60 text-xs" data-id="element-387">
                  Arizona • Verified Buyer
                </div>
              </div>
            </div>
          </motion.div>

          {/* Testimonial 3 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-white/5 p-6" data-id="element-388">
            <div className="flex gap-1 mb-4" data-id="element-389">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-orange-accent text-orange-accent" data-id="element-390" />)}
            </div>
            <p className="font-inter text-concrete leading-relaxed mb-4" data-id="element-391">
              "Former LEO here. I've seen what happens when criminals know where
              the safe is.{' '}
              <span className="text-white" data-id="element-392">
                They come back with tools and time.
              </span>{' '}
              This method eliminates that threat entirely. Should be standard
              practice for every gun owner."
            </p>
            <div className="flex items-center gap-3" data-id="element-393">
              <div className="w-10 h-10 bg-orange-accent/20 rounded-full flex items-center justify-center" data-id="element-394">
                <span className="font-bebas text-orange-accent" data-id="element-395">D.K.</span>
              </div>
              <div data-id="element-396">
                <div className="font-inter text-white text-sm font-semibold" data-id="element-397">
                  David K.
                </div>
                <div className="font-inter text-concrete/60 text-xs" data-id="element-398">
                  Florida • Verified Buyer
                </div>
              </div>
            </div>
          </motion.div>

          {/* Testimonial 4 */}
          <motion.div variants={cardVariants} className="bg-dark-card border border-white/5 p-6" data-id="element-399">
            <div className="flex gap-1 mb-4" data-id="element-400">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-orange-accent text-orange-accent" data-id="element-401" />)}
            </div>
            <p className="font-inter text-concrete leading-relaxed mb-4" data-id="element-402">
              "The decoy safe bonus alone was worth the price.{' '}
              <span className="text-white" data-id="element-403">
                I put a cheap Stack-On in the closet with some old watches.
              </span>{' '}
              Real collection is under the workshop floor. Peace of mind I
              haven't had in years."
            </p>
            <div className="flex items-center gap-3" data-id="element-404">
              <div className="w-10 h-10 bg-orange-accent/20 rounded-full flex items-center justify-center" data-id="element-405">
                <span className="font-bebas text-orange-accent" data-id="element-406">R.S.</span>
              </div>
              <div data-id="element-407">
                <div className="font-inter text-white text-sm font-semibold" data-id="element-408">
                  Robert S.
                </div>
                <div className="font-inter text-concrete/60 text-xs" data-id="element-409">
                  Montana • Verified Buyer
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}